// =================== ONLINE SERVICES PAGE (WITH DELIVERY ADDRESS) ===================

import React, { useState, useEffect } from "react";
import {
  Loader2,
  CheckCircle,
  AlertCircle,
  Camera,
  Sparkles,
  MessageCircleHeart,
  Gift,
  Tv,
} from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const OnlineServicesPage = () => {
  const [selectedService, setSelectedService] = useState("live-darshan");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [razorpayLoading, setRazorpayLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [bookingForm, setBookingForm] = useState({
    service: "",
    name: "",
    email: "",
    phone: "",
    bookingDate: "",
    bookingTime: "",
    specialRequirements: "",
    address: "", // 👈 NEW
  });

  // ================= SERVICES =================
  const onlineServices = [
    {
      id: "live-darshan",
      title: "Live Darshan",
      icon: Tv,
      description: "Watch live temple prayers from anywhere",
      status: "Available 24/7",
      priceLabel: "Free",
      amount: 0,
    },
    {
      id: "virtual-puja",
      title: "Virtual Puja Booking",
      icon: Sparkles,
      description: "Book personalized pujas performed on your behalf",
      status: "Book in advance",
      priceLabel: "₹500",
      amount: 500,
    },
    {
      id: "online-consultation",
      title: "Online Spiritual Consultation",
      icon: MessageCircleHeart,
      description: "Video consultation with spiritual guides",
      status: "By appointment",
      priceLabel: "₹300",
      amount: 300,
    },
    {
      id: "digital-prasadam",
      title: "Digital Prasadam Delivery",
      icon: Gift,
      description: "Sacred prasadam delivered to your doorstep",
      status: "Available in city",
      priceLabel: "₹200",
      amount: 200,
    },
  ];

  // ================= LOAD RAZORPAY =================
  useEffect(() => {
    if (window.Razorpay) {
      setRazorpayLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    script.onerror = () => setRazorpayLoaded(false);
    document.body.appendChild(script);
  }, []);

  // ================= FETCH SLOTS =================
  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/available-slots/${date}`,
      );
      const result = await response.json();

      if (result.success) {
        setAvailableSlots(result.data);
      } else {
        setAvailableSlots([]);
      }
    } catch (err) {
      console.error("Slot fetch error:", err);
      setAvailableSlots([]);
    }
    setLoadingSlots(false);
  };

  // ================= ON DATE CHANGE =================
  useEffect(() => {
    if (!bookingForm.bookingDate || selectedService === "live-darshan") {
      setAvailableSlots([]);
      return;
    }
    fetchAvailableSlots(bookingForm.bookingDate);
  }, [bookingForm.bookingDate, selectedService]);

  // ================= SELECT SERVICE =================
  const handleServiceSelect = (id) => {
    const s = onlineServices.find((x) => x.id === id);

    setSelectedService(id);
    setSubmitStatus(null);

    setBookingForm({
      service: s?.title || "",
      name: "",
      email: "",
      phone: "",
      bookingDate: "",
      bookingTime: "",
      specialRequirements: "",
      address: "", // reset address when switching
    });
  };

  // ================= INPUT CHANGE =================
  const handleInputChange = (e) => {
    setBookingForm({
      ...bookingForm,
      [e.target.name]: e.target.value,
    });
  };

  // helper: merge address + specialRequirements for DB
  const buildSpecialRequirements = () => {
    if (selectedService !== "digital-prasadam") {
      return bookingForm.specialRequirements || "";
    }

    const addr = bookingForm.address?.trim();
    const notes = bookingForm.specialRequirements?.trim();

    let text = "";
    if (addr) text += `Delivery Address:\n${addr}\n\n`;
    if (notes) text += `Additional Notes:\n${notes}`;
    return text || "Delivery Address not provided";
  };

  // ================= FREE BOOKING (NO PAYMENT) =================
  const handleFreeBooking = async (e) => {
    e.preventDefault();

    setSubmitStatus(null);

    const payload = {
      service: bookingForm.service,
      name: bookingForm.name,
      email: bookingForm.email,
      phone: bookingForm.phone,
      bookingDate: bookingForm.bookingDate,
      bookingTime: bookingForm.bookingTime,
      specialRequirements: buildSpecialRequirements(),
      paymentAmount: 0,
      paymentMethod: "free",
      deviceType: /Android|iPhone/i.test(navigator.userAgent)
        ? "mobile"
        : "desktop",
    };

    const response = await fetch(`${API_BASE_URL}/api/booking`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (result.success) {
      setSubmitStatus("success");
      setBookingForm({
        service: "",
        name: "",
        email: "",
        phone: "",
        bookingDate: "",
        bookingTime: "",
        specialRequirements: "",
        address: "",
      });
    } else {
      setSubmitStatus("error");
    }
  };

  // ================= RAZORPAY PAYMENT =================
  const handleRazorpayPayment = async (service) => {
    if (
      !bookingForm.name ||
      !bookingForm.email ||
      !bookingForm.phone ||
      !bookingForm.bookingDate
    ) {
      alert("Please fill all required fields before payment.");
      return;
    }

    if (service.id === "digital-prasadam" && !bookingForm.address.trim()) {
      alert("Please enter Delivery Address for Digital Prasadam Delivery.");
      return;
    }

    if (!razorpayLoaded) {
      alert("Payment gateway still loading… please try again.");
      return;
    }

    setRazorpayLoading(true);

    try {
      // CREATE ORDER
      const orderRes = await fetch(
        `${API_BASE_URL}/api/razorpay/create-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: service.amount,
            currency: "INR",
            notes: {
              service: service.title,
              name: bookingForm.name,
            },
          }),
        },
      );

      const orderData = await orderRes.json();

      if (!orderData.success) {
        alert(orderData.message);
        setRazorpayLoading(false);
        return;
      }

      // OPEN RAZORPAY
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: "INR",
        name: "Sri Kalikamundeshwari Temple",
        description: service.title,
        order_id: orderData.orderId,
        prefill: {
          name: bookingForm.name,
          email: bookingForm.email,
          contact: bookingForm.phone,
        },
        handler: async function (response) {
          const bookingPayload = {
            service: bookingForm.service,
            name: bookingForm.name,
            email: bookingForm.email,
            phone: bookingForm.phone,
            bookingDate: bookingForm.bookingDate,
            bookingTime: bookingForm.bookingTime,
            specialRequirements: buildSpecialRequirements(),
            paymentAmount: service.amount,
            paymentMethod: "razorpay",
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
            deviceType: /Android|iPhone/i.test(navigator.userAgent)
              ? "mobile"
              : "desktop",
          };

          const bookingRes = await fetch(`${API_BASE_URL}/api/booking`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(bookingPayload),
          });

          const result = await bookingRes.json();

          if (result.success) {
            setSubmitStatus("success");
          } else {
            setSubmitStatus("error");
          }

          setRazorpayLoading(false);
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Razorpay Error:", err);
      setSubmitStatus("error");
      setRazorpayLoading(false);
    }
  };

  const minDate = new Date().toISOString().split("T")[0];

  // ========================== UI SECTION ==========================

  return (
    <div className="animate-fadeIn py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* HEADER */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800">Online Services</h1>
          <p className="text-gray-600 text-lg mt-2">
            Experience divine blessings from your home
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT MENU */}
          <div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-bold text-orange-600 mb-4">
                Available Services
              </h2>

              {onlineServices.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    onClick={() => handleServiceSelect(s.id)}
                    className={`w-full text-left p-3 rounded-lg mb-3 ${
                      selectedService === s.id
                        ? "bg-orange-500 text-white"
                        : "bg-gray-100 hover:bg-orange-100"
                    }`}
                  >
                    <span className="mr-2 inline-flex align-middle">
                      <Icon className="h-5 w-5" strokeWidth={1.8} />
                    </span>
                    {s.title}
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow">
              {/* SHOW SERVICE CONTENT */}
              {onlineServices.map((service) =>
                selectedService === service.id ? (
                  <div key={service.id}>
                    {/* Title + Amount */}
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                      <h2 className="text-3xl font-bold text-gray-800">
                        {service.title}
                      </h2>
                      <span
                        className={`text-xl font-semibold ${
                          service.amount > 0
                            ? "text-green-600"
                            : "text-blue-600"
                        }`}
                      >
                        {service.amount > 0
                          ? `₹${service.amount}`
                          : "Free Service"}
                      </span>
                    </div>

                    <p className="text-gray-600 mb-6">{service.description}</p>

                    {/* SUCCESS */}
                    {submitStatus === "success" && (
                      <div className="p-4 bg-green-100 border border-green-300 rounded mb-4">
                        <CheckCircle className="inline mr-2 text-green-600" />
                        Booking confirmed! A confirmation email has been sent.
                      </div>
                    )}

                    {/* ERROR */}
                    {submitStatus === "error" && (
                      <div className="p-4 bg-red-100 border border-red-300 rounded mb-4">
                        <AlertCircle className="inline mr-2 text-red-600" />
                        Booking failed. Please try again.
                      </div>
                    )}

                    {/* LIVE DARSHAN */}
                    {service.id === "live-darshan" && (
                      <div className="bg-black text-white h-64 flex flex-col items-center justify-center rounded">
                        <Camera
                          className="h-16 w-16 text-white"
                          strokeWidth={1.6}
                        />
                        <p className="mt-4">Live Stream Available</p>
                      </div>
                    )}

                    {/* BOOKING FORM */}
                    {service.id !== "live-darshan" && (
                      <form
                        onSubmit={
                          service.amount === 0
                            ? handleFreeBooking
                            : (e) => e.preventDefault()
                        }
                      >
                        {/* Name, Email, Phone, Date */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <input
                            name="name"
                            className="border p-3 rounded w-full"
                            placeholder="Full Name"
                            required
                            value={bookingForm.name}
                            onChange={handleInputChange}
                          />
                          <input
                            name="email"
                            type="email"
                            className="border p-3 rounded w-full"
                            placeholder="Email"
                            required
                            value={bookingForm.email}
                            onChange={handleInputChange}
                          />
                          <input
                            name="phone"
                            className="border p-3 rounded w-full"
                            placeholder="Phone Number"
                            required
                            value={bookingForm.phone}
                            onChange={handleInputChange}
                          />
                          <input
                            type="date"
                            name="bookingDate"
                            min={minDate}
                            className="border p-3 rounded w-full"
                            required
                            value={bookingForm.bookingDate}
                            onChange={handleInputChange}
                          />
                        </div>

                        {/* Delivery Address – ONLY for Digital Prasadam */}
                        {service.id === "digital-prasadam" && (
                          <div className="mb-4">
                            <label className="block text-gray-700 mb-2">
                              Delivery Address *
                            </label>
                            <textarea
                              name="address"
                              rows="3"
                              className="border p-3 w-full rounded"
                              placeholder="House / Flat, Street, Landmark, City, PIN code"
                              required
                              value={bookingForm.address}
                              onChange={handleInputChange}
                            />
                          </div>
                        )}

                        {/* TIME SLOTS */}
                        {bookingForm.bookingDate && (
                          <div className="mb-4">
                            <label className="block text-gray-600 mb-2">
                              Select Time Slot
                            </label>

                            {loadingSlots ? (
                              <p>Loading slots...</p>
                            ) : availableSlots.length > 0 ? (
                              <div className="grid grid-cols-3 gap-2">
                                {availableSlots.map((slot) => (
                                  <button
                                    type="button"
                                    key={slot}
                                    onClick={() =>
                                      setBookingForm({
                                        ...bookingForm,
                                        bookingTime: slot,
                                      })
                                    }
                                    className={`p-2 border rounded ${
                                      bookingForm.bookingTime === slot
                                        ? "bg-orange-500 text-white"
                                        : "bg-gray-100"
                                    }`}
                                  >
                                    {slot}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500">
                                No slots available for this date.
                              </p>
                            )}
                          </div>
                        )}

                        {/* SPECIAL REQUIREMENTS */}
                        <textarea
                          name="specialRequirements"
                          rows="3"
                          className="border p-3 w-full rounded mb-4"
                          placeholder="Special Requirements (optional)"
                          value={bookingForm.specialRequirements}
                          onChange={handleInputChange}
                        />

                        {/* PAID SERVICE BUTTON */}
                        {service.amount > 0 && (
                          <button
                            type="button"
                            onClick={() => handleRazorpayPayment(service)}
                            className="bg-green-600 text-white w-full p-3 rounded font-bold"
                          >
                            {razorpayLoading
                              ? "Processing..."
                              : `Pay ₹${service.amount} & Book`}
                          </button>
                        )}

                        {/* FREE SERVICE BUTTON */}
                        {service.amount === 0 && (
                          <button
                            type="submit"
                            className="bg-orange-600 text-white w-full p-3 rounded font-bold"
                          >
                            Book Now
                          </button>
                        )}
                      </form>
                    )}
                  </div>
                ) : null,
              )}
            </div>
          </div>
        </div>

        {/* Backend Info */}
        <div className="mt-10 p-5 bg-gray-100 rounded">
          <b>API:</b> {API_BASE_URL}
          <br />
          Slots API working: <span className="text-green-600 font-bold">✓</span>
        </div>
      </div>
    </div>
  );
};

export default OnlineServicesPage;
